<template>
  <v-dialog :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
      </div>

      <v-card-text class="pa-4">
        <label class="label-input">Section Name</label>
        <v-row>
          <v-col cols="12" md="8"><v-text-field v-model="fields.title" label="Name"/></v-col>
          <v-col cols="12" md="4"><v-checkbox v-model="fields.enableAnchor" label="Enable Anchor"/></v-col>
        </v-row>

        <label class="label-input">Header</label>
        <ckeditor :editor="editor" v-model="fields.header" :config="editorConfig" />

        <div class="mt-6 group-item">
          <v-checkbox
            v-model="fields.containerFullWidth"
            class="mb-4"
            label="Container Full Width"
            dense
            hide-details
          />
          <p class="text-caption">Slides Per View is based on the description container width.</p>
          <v-row>
            <v-col v-for="viewport in ['mobile', 'tablet', 'desktop']" :key="viewport" cols="12" md="4">
              <v-text-field
                v-model.number="fields.breakpoints[viewport].slidesPerView"
                :label="viewport"
                type="number"
                min="0.1"
                step="0.1"
                outlined
                dense
                hide-details
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <ColorPicker
                v-model="fields.cardBgColor"
                name="tryher-grid-percent-card-bg"
                label="Card Background Color"
                default-color="#FFFFFF"
              />
            </v-col>
            <v-col cols="12" md="6">
              <ColorPicker
                v-model="fields.cardTextColor"
                name="tryher-grid-percent-card-text"
                label="Card Text Color"
                default-color="#000000"
              />
            </v-col>
            <v-col cols="12" md="6">
              <ColorPicker
                v-model="fields.percentLineColor"
                name="tryher-grid-percent-line"
                label="Percent Line Color"
                default-color="#FF6099"
              />
            </v-col>
            <v-col cols="12" md="6">
              <ColorPicker
                v-model="fields.percentTextColor"
                name="tryher-grid-percent-text"
                label="Percent Text Color"
                default-color="#FFFFFF"
              />
            </v-col>
          </v-row>
        </div>

        <div class="mt-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <label class="label-input mb-0">Items</label>
            <v-btn small color="primary" @click="handleAddItem"><v-icon size="16">mdi-plus</v-icon>Add Item</v-btn>
          </div>
          <v-expansion-panels accordion>
            <v-expansion-panel v-for="(item, index) in fields.listItem" :key="index">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center">
                  <div class="line-clamp-1" v-html="item.content.replace(/<[^>]*>/g, '').trim()"></div>
                  <v-btn icon @click.stop="handleRemoveItem(index)">
                    <v-icon color="#ff6c6c">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <label class="d-block mb-2">Image</label>
                <UploadImage
                  :image="{ imageSrc: item.image }"
                  :height="`200`"
                  :typeUpload="'image'"
                  @callback="obj => handleUploadImage(obj, index)"
                  @callbackDelete="handleRemoveImage(index)"
                />
                <v-text-field
                  v-model.number="item.percent"
                  class="mt-5"
                  label="Percent"
                  type="number"
                  min="0"
                  max="100"
                  suffix="%"
                  outlined
                  dense
                />
                <label class="d-block mb-2">Content</label>
                <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <SectionSettingsContainerFuild
          class="mt-6"
          :settings="fields.settings"
          :containerPage="fields.containerPage"
          :showBackgroundImage="false"
          :showContainerPage="false"
        />
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer />
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { updateHtmlFromData } from '@/views/website/products/components/description/common/html/update';
import { replaceImageUrl } from '../common/utils.js';
import ColorPicker from './common/color-picker.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const defaultItem = () => ({
  image: replaceImageUrl('https://cdn.skyfastimg.com/images/2026/07/22/image-6a608852481fbd35524bd5aa.webp?w=1000'),
  percent: 97,
  content: '<p>Content</p>',
});

export default {
  name: 'TryherGridPercent',
  components: { UploadImage, ColorPicker, SectionSettingsContainerFuild },
  props: {
    sectionEditor: { type: Object, required: true },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Tryher grid percent',
        enableAnchor: false,
        header: '',
        breakpoints: {
          mobile: { slidesPerView: 1.5 },
          tablet: { slidesPerView: 3 },
          desktop: { slidesPerView: 3 },
        },
        percentLineColor: '#FF6099',
        percentTextColor: '#FFFFFF',
        cardBgColor: '#FFFFFF',
        cardTextColor: '#000000',
        containerFullWidth: false,
        listItem: [],
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FFF7FC',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: { enableStyle: false, background: '#FFFFFF' },
      },
    };
  },
  mounted() {
    const extract = this.sectionEditor.sectionExtract || {};
    this.fields = {
      ...this.fields,
      ...extract,
      breakpoints: {
        mobile: { ...this.fields.breakpoints.mobile, ...((extract.breakpoints || {}).mobile || {}) },
        tablet: { ...this.fields.breakpoints.tablet, ...((extract.breakpoints || {}).tablet || {}) },
        desktop: { ...this.fields.breakpoints.desktop, ...((extract.breakpoints || {}).desktop || {}) },
      },
      settings: { ...this.fields.settings, ...(extract.settings || {}) },
      containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
      listItem: (extract.listItem || []).map(item => ({ ...defaultItem(), ...item })),
    };
  },
  methods: {
    handleAddItem() {
      this.fields.listItem.push(defaultItem());
    },
    handleRemoveItem(index) {
      this.fields.listItem.splice(index, 1);
    },
    handleUploadImage(obj, index) {
      this.fields.listItem[index].image = obj.imageSrc;
    },
    handleRemoveImage(index) {
      this.fields.listItem[index].image = '';
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const html = updateHtmlFromData.sectionTryherGridPercent(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', { ...this.sectionEditor.section, title: this.fields.title, html });
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .ck-editor__editable_inline {
  min-height: 100px !important;
}
.label-input {
  display: block;
  margin-bottom: 10px;
  color: #000;
  font-size: 18px;
  font-weight: 500;
}
.group-item {
  padding: 15px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
}
.header-modal,
.footer-modal {
  position: sticky;
  left: 0;
  z-index: 999;
  padding: 0 16px;
  background: #fff;
  box-shadow: 0 0 12px #0000000f;
}
.header-modal {
  top: 0;
}
.footer-modal {
  bottom: 0;
}
</style>
